// Roles & Access — who sees which tool. Spider diagram per access level + matrix view,
// per-role toggles (saved to role_permissions) and a one-page export for the client's IT.
import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Download, RotateCcw, Loader2, Grid3X3, Network, Check, X, Minus, Clock, Copy, KeyRound } from "lucide-react";
import "./RolesAccess.css";

const API = process.env.REACT_APP_BACKEND_URL;

function polar(cx, cy, radius, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}

const STATE_LABEL = { on: "On", soon: "Coming soon", off: "Off" };
// A tool applies to a role if it's a carousel tile for a carousel role, or listed as applicable for a fixed-landing role.
const applies = (role, tool) => (role.carousel && tool.tile !== false) || (role.applicable || []).includes(tool.key);
const featuresOf = (data, toolKey) => (data.features || []).filter((f) => f.tool === toolKey);
const NEXT = { off: "on", on: "off", soon: "on" };

export default function RolesAccess() {
  const [data, setData] = useState(null);
  const [roleKey, setRoleKey] = useState("agent");
  const [view, setView] = useState("spider");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/access/matrix`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load roles");
    }
  };
  useEffect(() => { load(); }, []);

  const role = useMemo(() => data?.roles.find((r) => r.key === roleKey) || null, [data, roleKey]);

  const setTool = async (rKey, toolKey, state) => {
    if (!data) return;
    const target = data.roles.find((r) => r.key === rKey);
    const tools = { ...target.tools, [toolKey]: state };
    setData({ ...data, roles: data.roles.map((r) => (r.key === rKey ? { ...r, tools, customised: true } : r)) });
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/access/roles/${rKey}`, { tools });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
      load();
    } finally {
      setBusy(false);
    }
  };

  const resetRole = async () => {
    if (!role) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/api/admin/access/roles/${role.key}`);
      setData(r.data);
      toast.success(`${role.label} back to defaults`);
    } catch (e) {
      toast.error("Couldn't reset");
    } finally {
      setBusy(false);
    }
  };

  const exportForIT = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/api/admin/access/export`);
      const md = r.data.markdown;
      const blob = new Blob([md], { type: "text/markdown" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "nichesocial-access-levels.md";
      a.click();
      URL.revokeObjectURL(a.href);
      try { await navigator.clipboard.writeText(md); toast.success("Access list downloaded and copied"); } catch { toast.success("Access list downloaded"); }
    } catch (e) {
      toast.error("Couldn't build the export");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <div className="ra-loading" data-testid="roles-access-loading"><Loader2 size={16} className="ra-spin" /> Loading roles…</div>;

  return (
    <div className="ra-shell" data-testid="roles-access">
      <div className="ra-head">
        <div>
          <h2 className="ra-title"><Shield size={17} /> Roles &amp; Access</h2>
          <p className="ra-sub">Pick a level, click a spoke to switch a tool on or off for everyone at that level. Per-person exceptions live on the user row.</p>
        </div>
        <div className="ra-actions">
          <div className="ra-seg" role="tablist">
            <button role="tab" aria-selected={view === "spider"} className={`ra-seg-btn ${view === "spider" ? "is-on" : ""}`} onClick={() => setView("spider")} data-testid="roles-access-view-spider"><Network size={13} /> Spider</button>
            <button role="tab" aria-selected={view === "matrix"} className={`ra-seg-btn ${view === "matrix" ? "is-on" : ""}`} onClick={() => setView("matrix")} data-testid="roles-access-view-matrix"><Grid3X3 size={13} /> Matrix</button>
            <button role="tab" aria-selected={view === "it"} className={`ra-seg-btn ${view === "it" ? "is-on" : ""}`} onClick={() => setView("it")} data-testid="roles-access-view-it"><KeyRound size={13} /> IDs for IT</button>
          </div>
          <button className="ra-btn" onClick={exportForIT} disabled={exporting} data-testid="roles-access-export">{exporting ? <Loader2 size={13} className="ra-spin" /> : <Download size={13} />} Export for IT</button>
        </div>
      </div>

      <div className="ra-roles" data-testid="roles-access-role-tabs">
        {data.roles.map((r) => (
          <button key={r.key} className={`ra-role ${r.key === roleKey ? "is-on" : ""}`} onClick={() => setRoleKey(r.key)} data-testid={`roles-access-role-${r.key}`}>
            {r.label}
            <span className="ra-role-count">{r.user_count}</span>
            {r.customised && <span className="ra-dot" title="Customised from defaults" />}
          </button>
        ))}
      </div>

      {view === "spider" && role && (
        <div className="ra-board">
          <Spider role={role} tools={data.tools} onToggle={(k) => setTool(role.key, k, NEXT[role.tools[k]])} />
          <RolePanel role={role} tools={data.tools} features={data.features} busy={busy} onSet={(k, s) => setTool(role.key, k, s)} onReset={resetRole} />
        </div>
      )}

      {view === "matrix" && <Matrix data={data} onCycle={(rKey, k, cur, direct) => setTool(rKey, k, direct ? cur : NEXT[cur])} />}

      {view === "it" && <ItHandover data={data} />}
    </div>
  );
}

function Spider({ role, tools, onToggle }) {
  const VB = 720, cx = 360, cy = 360, HUB_R = 84, SPOKE_LEN = 246, NODE_R = 30;
  const fixed = !role.carousel;
  const items = tools.filter((t) => applies(role, t)).map((t) => ({ ...t, state: role.tools[t.key], readOnly: false }))
    .concat(fixed ? (role.fixed_features || []).map((f, i) => ({ key: `fixed-${i}`, label: f, state: "on", readOnly: true })) : []);
  const onCount = items.filter((i) => i.state === "on").length;
  return (
    <div className="ra-diagram-wrap" data-testid="roles-access-diagram">
      <svg viewBox={`0 0 ${VB} ${VB}`} className="ra-diagram" key={role.key} role="img" aria-label={`${role.label} access diagram`}>
        <defs>
          <radialGradient id="ra-hub-bg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#96C11F" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#96C11F" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={SPOKE_LEN + NODE_R + 10} className="ra-orbit" />
        <circle cx={cx} cy={cy} r={SPOKE_LEN - 24} className="ra-orbit ra-orbit-inner" />
        <circle cx={cx} cy={cy} r={HUB_R + 60} fill="url(#ra-hub-bg)" />

        {items.map((it, idx) => {
          const angle = (idx * 360) / Math.max(items.length, 1);
          const start = polar(cx, cy, HUB_R - 2, angle);
          const end = polar(cx, cy, SPOKE_LEN - NODE_R + 2, angle);
          const node = polar(cx, cy, SPOKE_LEN, angle);
          return (
            <g key={it.key} className={`ra-spoke is-${it.state} ${it.readOnly ? "is-fixed" : ""}`} style={{ "--ra-delay": `${0.06 + idx * 0.04}s` }}
               onClick={() => !it.readOnly && onToggle(it.key)} data-testid={`roles-access-spoke-${it.key}`}>
              <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} className="ra-line" />
              <line x1={start.x} y1={start.y} x2={node.x} y2={node.y} className="ra-hit" />
              <g className="ra-node-group">
                <circle cx={node.x} cy={node.y} r={NODE_R + 5} className="ra-node-halo" />
                <circle cx={node.x} cy={node.y} r={NODE_R} className="ra-node" />
                <g transform={`translate(${node.x - 9} ${node.y - 9})`} className="ra-node-icon">
                  {it.state === "on" && <Check size={18} strokeWidth={3} />}
                  {it.state === "soon" && <Clock size={18} strokeWidth={2.5} />}
                  {it.state === "off" && <Minus size={18} strokeWidth={2.5} />}
                </g>
              </g>
              <SpokeLabel cx={cx} cy={cy} angle={angle} radius={SPOKE_LEN + NODE_R + 14} label={it.label} />
            </g>
          );
        })}

        <g className="ra-hub">
          <circle cx={cx} cy={cy} r={HUB_R} className="ra-hub-ring" />
          <text x={cx} y={cy - 12} textAnchor="middle" dominantBaseline="middle" className="ra-hub-title">{role.label}</text>
          <text x={cx} y={cy + 10} textAnchor="middle" dominantBaseline="middle" className="ra-hub-sub">{`${onCount} ${fixed ? "section" : "tool"}${onCount === 1 ? "" : "s"} on`}</text>
          <text x={cx} y={cy + 28} textAnchor="middle" dominantBaseline="middle" className="ra-hub-sub2">{role.user_count} user{role.user_count === 1 ? "" : "s"}</text>
        </g>
      </svg>
      <div className="ra-legend">
        <span><i className="ra-key is-on" /> On</span>
        <span><i className="ra-key is-soon" /> Coming soon (greyed card)</span>
        <span><i className="ra-key is-off" /> Off</span>
      </div>
    </div>
  );
}

function SpokeLabel({ cx, cy, angle, radius, label }) {
  const pos = polar(cx, cy, radius, angle);
  let anchor = "middle";
  if (angle > 12 && angle < 168) anchor = "start";
  else if (angle > 192 && angle < 348) anchor = "end";
  let dy = 4;
  if (angle < 30 || angle > 330) dy = -4;
  if (angle > 150 && angle < 210) dy = 14;
  const text = label.length > 30 ? `${label.slice(0, 28)}…` : label;
  return <text x={pos.x} y={pos.y + dy} textAnchor={anchor} dominantBaseline="middle" className="ra-node-label">{text}</text>;
}

function RolePanel({ role, tools, features = [], busy, onSet, onReset }) {
  const groups = useMemo(() => {
    const order = [], by = {};
    tools.filter((t) => applies(role, t)).forEach((t) => { if (!by[t.group]) { by[t.group] = []; order.push(t.group); } by[t.group].push(t); });
    return order.map((g) => ({ group: g, tools: by[g] }));
  }, [tools, role]);
  return (
    <aside className="ra-panel" data-testid="roles-access-panel">
      <div className="ra-panel-head">
        <div>
          <span className="ra-eyebrow">{role.system_roles.map((s) => `role: ${s}`).join(" · ")}</span>
          <h3>{role.label}</h3>
          <p>{role.description}</p>
        </div>
        {role.customised && (
          <button className="ra-btn ghost small" onClick={onReset} disabled={busy} data-testid="roles-access-reset"><RotateCcw size={12} /> Reset to defaults</button>
        )}
      </div>
      <div className="ra-landing"><strong>Lands on</strong> {role.landing}</div>
      {role.fixed_features?.length > 0 && (
        <ul className="ra-fixed">
          {role.fixed_features.map((f) => <li key={f}><Check size={12} /> {f}</li>)}
        </ul>
      )}
      <div className="ra-tool-groups">
        {groups.map(({ group, tools: ts }) => (
          <div key={group} className="ra-tool-group">
            <h4>{group}</h4>
            {ts.map((t) => {
              const state = role.tools[t.key];
              const changed = state !== role.defaults[t.key];
              const feats = features.filter((f) => f.tool === t.key);
              return (
                <div key={t.key}>
                  <div className={`ra-tool-row is-${state}`} data-testid={`roles-access-row-${t.key}`}>
                    <div className="ra-tool-text">
                      <span className="ra-tool-name">{t.label}{changed && <span className="ra-changed">changed</span>}</span>
                      <span className="ra-tool-blurb">{t.blurb}</span>
                    </div>
                    <div className="ra-tri" role="radiogroup" aria-label={`${t.label} for ${role.label}`}>
                      {["off", "soon", "on"].map((s) => (
                        <button key={s} type="button" role="radio" aria-checked={state === s} className={`ra-tri-btn val-${s} ${state === s ? "is-active" : ""}`} onClick={() => onSet(t.key, s)} disabled={busy} data-testid={`roles-access-set-${t.key}-${s}`} title={STATE_LABEL[s]}>
                          {s === "on" ? <Check size={12} /> : s === "soon" ? <Clock size={12} /> : <X size={12} />}
                        </button>
                      ))}
                    </div>
                  </div>
                  {feats.length > 0 && (
                    <div className={`ra-features ${state === "on" ? "" : "is-parent-off"}`}>
                      {feats.map((f) => {
                        const fs = role.tools[f.key];
                        const fChanged = fs !== role.defaults[f.key];
                        return (
                          <div key={f.key} className={`ra-tool-row ra-feature-row is-${fs}`} data-testid={`roles-access-row-${f.key}`}>
                            <div className="ra-tool-text">
                              <span className="ra-tool-name">{f.label}{fChanged && <span className="ra-changed">changed</span>}</span>
                              <span className="ra-tool-blurb">{f.blurb}</span>
                            </div>
                            <button type="button" role="switch" aria-checked={fs === "on"} className={`ra-switch ${fs === "on" ? "is-on" : ""}`} onClick={() => onSet(f.key, fs === "on" ? "off" : "on")} disabled={busy} data-testid={`roles-access-set-${f.key}`} title={fs === "on" ? "On — click to switch off" : "Off — click to switch on"}>
                              <i />
                            </button>
                          </div>
                        );
                      })}
                      {state !== "on" && <p className="ra-note">Sections only apply once {t.label} is on.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {!role.carousel && <p className="ra-note">This level lands on a fixed page rather than the tools carousel — only the sections above apply to it.</p>}
      {role.updated_at && <p className="ra-note">Last changed {new Date(role.updated_at).toLocaleString()} by {role.updated_by}</p>}
    </aside>
  );
}

function Matrix({ data, onCycle }) {
  return (
    <div className="ra-matrix-wrap" data-testid="roles-access-matrix">
      <table className="ra-matrix">
        <thead>
          <tr>
            <th>Tool</th>
            {data.roles.map((r) => <th key={r.key} title={r.landing}><span>{r.label}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {data.tools.map((t) => (
            <Fragment key={t.key}>
            <tr>
              <td className="ra-matrix-tool"><strong>{t.label}</strong><span>{t.group}</span></td>
              {data.roles.map((r) => {
                const s = r.tools[t.key];
                if (!applies(r, t)) return <td key={r.key} className="ra-cell is-na" title={`Not part of what a ${r.label} sees (lands on ${r.landing})`}>·</td>;
                return (
                  <td key={r.key} className={`ra-cell is-${s}`}>
                    <button type="button" className="ra-cell-btn" onClick={() => onCycle(r.key, t.key, s)} title={`${r.label} · ${t.label}: ${STATE_LABEL[s]} — click to change`} data-testid={`roles-access-cell-${r.key}-${t.key}`}>
                      {s === "on" ? <Check size={14} strokeWidth={3} /> : s === "soon" ? <Clock size={14} /> : <Minus size={14} />}
                    </button>
                  </td>
                );
              })}
            </tr>
            {featuresOf(data, t.key).map((f) => (
              <tr key={f.key} className="ra-matrix-feature">
                <td className="ra-matrix-tool"><strong>{f.label}</strong><span>section of {t.label}</span></td>
                {data.roles.map((r) => {
                  if (!applies(r, t)) return <td key={r.key} className="ra-cell is-na">·</td>;
                  const parentOn = r.tools[t.key] === "on";
                  const s = r.tools[f.key];
                  return (
                    <td key={r.key} className={`ra-cell is-${s} ${parentOn ? "" : "is-dim"}`}>
                      <button type="button" className="ra-cell-btn small" onClick={() => onCycle(r.key, f.key, s === "on" ? "off" : "on", true)} title={`${r.label} · ${f.label}: ${STATE_LABEL[s]}${parentOn ? "" : ` (${t.label} is off)`} — click to change`} data-testid={`roles-access-cell-${r.key}-${f.key}`}>
                        {s === "on" ? <Check size={12} strokeWidth={3} /> : <Minus size={12} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            </Fragment>
          ))}
          <tr className="ra-matrix-landing">
            <td className="ra-matrix-tool"><strong>Lands on</strong></td>
            {data.roles.map((r) => <td key={r.key}>{r.landing}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Per-account exceptions — used from the Admin → Users row.
export function UserAccessModal({ user, onClose }) {
  const [eff, setEff] = useState(null);
  const [tools, setTools] = useState([]);
  const [features, setFeatures] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/api/admin/access/users/${user.id}`), axios.get(`${API}/api/admin/access/matrix`)])
      .then(([a, m]) => { setEff(a.data); setTools(m.data.tools); setFeatures(m.data.features || []); })
      .catch(() => toast.error("Couldn't load this user's access"));
  }, [user.id]);

  const set = async (key, value) => {
    const next = { ...(eff.overrides || {}) };
    if (value === "inherit") delete next[key]; else next[key] = value;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/access/users/${user.id}`, { tool_overrides: next });
      setEff(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ra-modal-bg" onClick={onClose} data-testid="user-access-modal">
      <div className="ra-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ra-modal-head">
          <div>
            <span className="ra-eyebrow">Per-account access</span>
            <h3>{user.name}</h3>
            <p>{user.email} · {eff?.role_label || user.role}{eff?.demo_user ? " · Demo user (Social Video on)" : ""}</p>
          </div>
          <button className="ra-btn ghost small" onClick={onClose} data-testid="user-access-close"><X size={13} /></button>
        </div>
        {!eff ? <div className="ra-loading"><Loader2 size={14} className="ra-spin" /> Loading…</div> : (
          <div className="ra-modal-list">
            {[...tools, ...features].map((t) => {
              const isFeature = !!t.tool;
              const state = isFeature ? eff.features?.[t.key] : eff.tools.find((x) => x.key === t.key)?.state;
              const ov = eff.overrides?.[t.key] || "inherit";
              return (
                <div key={t.key} className={`ra-tool-row is-${state} ${isFeature ? "ra-feature-row" : ""}`} data-testid={`user-access-row-${t.key}`}>
                  <div className="ra-tool-text">
                    <span className="ra-tool-name">{t.label}{ov !== "inherit" && <span className="ra-changed">exception</span>}</span>
                    <span className="ra-tool-blurb">{isFeature ? `Section of ${tools.find((x) => x.key === t.tool)?.label || t.tool} · ` : ""}Effective: {STATE_LABEL[state] || state}</span>
                  </div>
                  <div className="ra-tri ra-tri-wide" role="radiogroup">
                    {[["inherit", "Role"], ["on", "On"], ["off", "Off"]].map(([v, lbl]) => (
                      <button key={v} type="button" role="radio" aria-checked={ov === v} className={`ra-tri-btn val-${v} ${ov === v ? "is-active" : ""}`} onClick={() => set(t.key, v)} disabled={saving} data-testid={`user-access-set-${t.key}-${v}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              );
            })}
            <p className="ra-note">"Role" follows whatever the level is set to in Roles &amp; Access. Exceptions stick to this person even if their role changes.</p>
          </div>
        )}
      </div>
    </div>
  );
}


const WMS_CLAIMS = [
  ["email", "required", "Matches the NicheSocial account"],
  ["wms_user_id", "required", "Becomes the primary match after first sign-in"],
  ["role", "required", "One of the ID values in the table above"],
  ["name", "optional", "Display name when we auto-create the account"],
  ["wms_branch_id", "Estate Agent · Branch Manager · Editor", "Puts them in the right branch"],
  ["wms_branch_ids", "Marketing Manager", "List of every branch they cover"],
  ["wms_client_id", "Brand Admin", "The brand they manage"],
  ["editor_admin", "Head of Media only", "true = Head of Media (Team tab, Order Search, reels unlocked)"],
  ["module", "optional", "Deep-link straight into a tool"],
];

function ItHandover({ data }) {
  const [copied, setCopied] = useState(false);
  const label = Object.fromEntries(data.tools.map((t) => [t.key, t.label]));
  const rows = data.roles.map((r) => {
    const on = data.tools.filter((t) => applies(r, t) && r.tools[t.key] === "on").map((t) => {
      const fs = featuresOf(data, t.key);
      if (!fs.length) return label[t.key];
      const onF = fs.filter((f) => r.tools[f.key] === "on").map((f) => f.label);
      return `${label[t.key]} (${onF.length ? onF.join(", ") : "no sections"})`;
    });
    return { ...r, sees: [...(r.fixed_features || []), ...on] };
  });

  const copy = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/access/export`);
      await navigator.clipboard.writeText(r.data.markdown);
      setCopied(true);
      toast.success("Copied — paste it straight into an email to IT");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="ra-it" data-testid="roles-access-it">
      <div className="ra-it-head">
        <div>
          <span className="ra-eyebrow">WMS single sign-on · handover</span>
          <h3>Access-level IDs for IT</h3>
          <p>Each WMS user maps to exactly one ID below. WMS puts it in the token's <code>role</code> claim; we sign them straight in as that level — no separate password. IT can rename any ID, just tell us what they'd rather send.</p>
        </div>
        <button className="ra-btn" onClick={copy} data-testid="roles-access-copy-it">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy to clipboard"}</button>
      </div>
      <div className="ra-it-table-wrap">
        <table className="ra-it-table" data-testid="roles-access-it-table">
          <thead><tr><th>Access level</th><th>ID (<code>role</code> claim)</th><th>NicheSocial role</th><th>Lands on</th><th>Sees</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-testid={`roles-access-it-row-${r.key}`}>
                <td><strong>{r.label}</strong>{r.key === "head_of_media" && <div className="ra-it-sub">Stored as <code>editor</code> + <code>editor_admin: true</code></div>}</td>
                <td><code className="ra-id">{r.sso_claim}</code></td>
                <td><code>{r.system_roles[0]}</code></td>
                <td>{r.landing}</td>
                <td className="ra-it-sees">{r.sees.join(" · ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ra-it-claims">
        <h4>What WMS sends alongside the ID</h4>
        <table className="ra-it-table small">
          <thead><tr><th>Claim</th><th>When</th><th>Used for</th></tr></thead>
          <tbody>
            {WMS_CLAIMS.map(([k, when, why]) => <tr key={k}><td><code className="ra-id">{k}</code></td><td>{when}</td><td>{why}</td></tr>)}
          </tbody>
        </table>
        <p className="ra-note">Plus the standard token fields: <code>iss: nichecom-wms</code> · <code>aud: nichesocial</code> · <code>iat</code> · <code>exp</code> (≤ 15 min) · <code>jti</code> single-use. Full contract: <code>GET /api/auth/sso/wms/spec</code>. The "Sees" column tracks whatever is switched on in the Spider / Matrix views, so the copy is always current.</p>
      </div>
    </div>
  );
}
